import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Collapse,
  Divider,
  Paper,
  Avatar,
  TextField,
  Button,
  Link as MuiLink,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from "@mui/material";
import {
  PlayCircleFilled,
  ExpandLess,
  ExpandMore,
  MenuBook,
  OndemandVideo,
  Quiz,
  Article,
  AssignmentTurnedIn,
  ChromeReaderMode,
  Reply,
  Lock,
  LockOpen,
  Menu,
  ChevronLeft,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { getCompleteCourseDetail } from "../../State/Course/Action";
import {
  getChapterComments,
  addChapterComment,
  addCommentReply,
} from "../../State/Comment/Action";
import PlayQuiz from "../Quiz/PlayQuiz";
import ShowArticle from "../Article/ShowArticle";
import { resetAllActions } from "../../State/Learner/Action";
import { addCourseToCart } from "../../State/Cart/Action";

const CourseCompleteDetail = () => {
  const { courseId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const course = useSelector((store) => store.course.course);
  const comments = useSelector(
    (store) => store.comment.chapterComments?.data || []
  );

  const [openModuleId, setOpenModuleId] = useState(null);
  const [selectedVideoUrl, setSelectedVideoUrl] = useState(null);
  const [chapterType, setChapterType] = useState("VIDEO");
  const [chapterId, setChapterId] = useState(null);
  const [newComment, setNewComment] = useState("");
  const [replyTexts, setReplyTexts] = useState({});
  const [showReplyBox, setShowReplyBox] = useState({});
  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
  const [lockedChapter, setLockedChapter] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    dispatch(getCompleteCourseDetail(courseId));
    // Scroll to top on load
    window.scrollTo(0, 0);
  }, [dispatch, courseId]);

  // Keep sidebar open by default on all screens

  useEffect(() => {
    resetAllActions();
    if (chapterId) {
      dispatch(getChapterComments(chapterId));
    }
  }, [dispatch, chapterId]);

  const handleModuleToggle = (moduleId) => {
    setOpenModuleId((prevId) => (prevId === moduleId ? null : moduleId));
  };

  const handleChapter = (chapter) => {
    // Check if chapter is previewable or if user has access
    if (chapter.previewAvailable) {
      setChapterId(chapter.id);
      setChapterType(chapter.type || "VIDEO");
      setSelectedVideoUrl(chapter.videoUrl || null);
      // Toggle sidebar when clicking on a video
      if ((chapter.type || "VIDEO") === "VIDEO") {
        setSidebarOpen(prev => !prev);
      }
    } else {
      // Show subscription dialog for locked chapters
      setLockedChapter(chapter);
      setSubscriptionDialogOpen(true);
    }
  };

  const handlePostComment = () => {
    if (newComment.trim() !== "") {
      const input = { commentText: newComment };
      dispatch(addChapterComment(chapterId, input)).then(() => {
        setNewComment("");
        dispatch(getChapterComments(chapterId));
      });
    }
  };

  const handlePostReply = (commentId) => {
    if (replyTexts[commentId]?.trim() !== "") {
      const input = { replyText: replyTexts[commentId] };
      dispatch(addCommentReply(commentId, input)).then(() => {
        setReplyTexts({ ...replyTexts, [commentId]: "" });
        setShowReplyBox({ ...showReplyBox, [commentId]: false });
        dispatch(getChapterComments(chapterId));
      });
    }
  };

  const handleSubscribeToCourse = async () => {
    // Close dialog
    setSubscriptionDialogOpen(false);
    setLockedChapter(null);
    // Add course to cart and navigate to cart page
    try {
      await dispatch(addCourseToCart(courseId));
      navigate('/show-my-cart');
    } catch (error) {
      console.error("Error adding course to cart:", error);
    }
  };

  const handleCloseSubscriptionDialog = () => {
    setSubscriptionDialogOpen(false);
    setLockedChapter(null);
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  if (!course) return <Typography>Loading...</Typography>;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Background decorative elements */}
      <Box
        sx={{
          position: "absolute",
          top: "-50%",
          right: "-50%",
          width: "200%",
          height: "200%",
          background: "radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)",
          animation: "float 20s ease-in-out infinite",
          "@keyframes float": {
            "0%, 100%": { transform: "translate(0, 0) rotate(0deg)" },
            "50%": { transform: "translate(-30px, -30px) rotate(180deg)" },
          },
        }}
      />
      
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          p: 0,
          maxWidth: "100%",
          mx: 0,
        }}
      >
        {/* Enhanced Header - Sticky to Top */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 100,
            textAlign: "center",
            mb: 0,
            p: 1.5,
            background: "linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.95) 100%)",
            backdropFilter: "blur(25px)",
            borderRadius: 0,
            boxShadow: "0 8px 32px rgba(102, 126, 234, 0.15), inset 0 1px 0 rgba(255,255,255,0.8)",
            border: "1px solid rgba(255,255,255,0.3)",
            borderBottom: "2px solid rgba(102, 126, 234, 0.2)",
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: "50%",
              transform: "translateX(-50%)",
              width: "60px",
              height: "3px",
              background: "linear-gradient(90deg, #667eea 0%, #764ba2 50%, #667eea 100%)",
              borderRadius: "0 0 8px 8px",
            },
          }}
        >
          <Box sx={{ position: "relative", zIndex: 2, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Tooltip title={sidebarOpen ? "Hide Sidebar" : "Show Sidebar"} arrow>
              <IconButton
                aria-label="toggle sidebar"
                onClick={toggleSidebar}
                sx={{
                  position: "absolute",
                  left: 12,
                  zIndex: 3,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "white",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)",
                    transform: "scale(1.05)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                <Menu />
              </IconButton>
            </Tooltip>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                mb: 0,
                fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" },
                textShadow: "0 2px 4px rgba(102, 126, 234, 0.1)",
                letterSpacing: "0.02em",
                position: "relative",
                pointerEvents: 'none',
                maxWidth: { xs: '70%', sm: '75%', md: '80%' },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: "-2px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: "40px",
                  height: "1px",
                  background: "linear-gradient(90deg, transparent 0%, #667eea 50%, transparent 100%)",
                },
              }}
            >
              🎓 {course.name}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ 
          display: "flex", 
          gap: 0, 
          flexDirection: { xs: "column", lg: "row" },
          alignItems: "flex-start",
          width: "100%"
        }}>
          {/* Toggleable Left Sidebar */}
          {sidebarOpen && (
            <Paper
              sx={{
                flex: { xs: 1, lg: "0 0 300px" },
                p: 0,
                borderRadius: 0,
                background: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(20px)",
                boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                border: "1px solid rgba(255,255,255,0.2)",
                overflow: "hidden",
                maxHeight: { xs: "unset", lg: "85vh" },
                display: "flex",
                flexDirection: "column",
                position: "relative",
                top: 0,
                transition: "all 0.3s ease",
              }}
            >
            {/* Sidebar Header */}
            <Box
              sx={{
                p: 2,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
                  <MenuBook sx={{ fontSize: "1.5rem" }} />
                  Course Modules
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                {course.modules?.length} modules • {course.modules?.reduce((acc, module) => acc + (module.chapters?.length || 0), 0)} chapters • {course.length || "0h 0m"}
              </Typography>
            </Box>

            {/* Scrollable Content */}
            <Box sx={{ flex: 1, overflow: "auto" }}>
              <List sx={{ p: 0 }}>
                {course.modules?.map((module, index) => (
                  <Box key={module.id}>
                      <ListItem
                      button
                      onClick={() => handleModuleToggle(module.id)}
                      sx={{
                        py: { xs: 1.5, md: 2 },
                        px: { xs: 1.5, md: 2 },
                        borderBottom: "1px solid rgba(0,0,0,0.05)",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          backgroundColor: "rgba(102, 126, 234, 0.08)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mr: 2,
                          color: "white",
                          fontWeight: "bold",
                        }}
                      >
                        {module.orderIndex ?? index + 1}
                      </Box>
                      <ListItemText
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#374151" }}>
                            {module.title}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.5 }}>
                            {module.length || "0h 0m"} • {module.chapters?.length || 0} chapters
                          </Typography>
                        }
                      />
                      <Box
                        sx={{
                          p: 1,
                          borderRadius: "50%",
                          backgroundColor: openModuleId === module.id ? "rgba(102, 126, 234, 0.1)" : "transparent",
                          transition: "all 0.3s ease",
                        }}
                      >
                        {openModuleId === module.id ? <ExpandLess /> : <ExpandMore />}
                      </Box>
                    </ListItem>
                    <Collapse
                      in={openModuleId === module.id}
                      timeout="auto"
                      unmountOnExit
                    >
                      <List component="div" disablePadding>
                        {module.chapters?.map((chapter, chapterIndex) => (
                          <ListItem
                            key={chapter.id}
                            sx={{
                              pl: { xs: 4, md: 6 },
                              pr: { xs: 1.5, md: 2 },
                              py: { xs: 1, md: 1.5 },
                              borderLeft: "3px solid",
                              borderLeftColor: chapter.previewAvailable ? "#10b981" : "#ef4444",
                              backgroundColor: chapter.previewAvailable ? "rgba(16, 185, 129, 0.05)" : "rgba(239, 68, 68, 0.05)",
                              transition: "all 0.3s ease",
                              "&:hover": {
                                backgroundColor: chapter.previewAvailable ? "rgba(16, 185, 129, 0.1)" : "rgba(239, 68, 68, 0.1)",
                              },
                            }}
                            secondaryAction={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {chapterIndex === 0 && (
                                  <Tooltip title={sidebarOpen ? 'Hide Sidebar' : 'Show Sidebar'} arrow>
                                    <IconButton
                                      edge="end"
                                      onClick={toggleSidebar}
                                      sx={{
                                        p: 1,
                                        borderRadius: '8px',
                                        backgroundColor: 'rgba(102, 126, 234, 0.08)',
                                        '&:hover': { backgroundColor: 'rgba(102, 126, 234, 0.16)' },
                                        transition: 'all 0.3s ease',
                                      }}
                                      aria-label="toggle sidebar"
                                    >
                                      <Menu sx={{ fontSize: '1.2rem' }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <IconButton
                                  edge="end"
                                  onClick={() => handleChapter(chapter)}
                                  title={
                                    chapter.previewAvailable
                                      ? "Click to preview"
                                      : "Subscribe to access this chapter"
                                  }
                                  sx={{
                                    p: 1,
                                    borderRadius: "50%",
                                    backgroundColor: chapter.previewAvailable ? "rgba(16, 185, 129, 0.1)" : "rgba(239, 68, 68, 0.1)",
                                    "&:hover": {
                                      backgroundColor: chapter.previewAvailable ? "rgba(16, 185, 129, 0.2)" : "rgba(239, 68, 68, 0.2)",
                                      transform: "scale(1.05)",
                                    },
                                    transition: "all 0.3s ease",
                                  }}
                                >
                                  {chapter.previewAvailable ? (
                                    <>
                                      {chapter.type === "VIDEO" && (
                                        <PlayCircleFilled sx={{ color: "#10b981", fontSize: "1.5rem" }} />
                                      )}
                                      {chapter.type === "QUIZ" && (
                                        <AssignmentTurnedIn sx={{ color: "#10b981", fontSize: "1.5rem" }} />
                                      )}
                                      {chapter.type === "ARTICLE" && (
                                        <ChromeReaderMode sx={{ color: "#10b981", fontSize: "1.5rem" }} />
                                      )}
                                    </>
                                  ) : (
                                    <Lock sx={{ color: "#ef4444", fontSize: "1.5rem" }} />
                                  )}
                                </IconButton>
                              </Box>
                            }
                          >
                            <Box sx={{ mr: 2, display: "flex", alignItems: "center" }}>
                              {chapter.type === "VIDEO" && (
                                <Box
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: "50%",
                                    backgroundColor: "rgba(102, 126, 234, 0.1)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <OndemandVideo sx={{ color: "#667eea", fontSize: "1rem" }} />
                                </Box>
                              )}
                              {chapter.type === "QUIZ" && (
                                <Box
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: "50%",
                                    backgroundColor: "rgba(139, 92, 246, 0.1)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <Quiz sx={{ color: "#8b5cf6", fontSize: "1rem" }} />
                                </Box>
                              )}
                              {chapter.type === "ARTICLE" && (
                                <Box
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: "50%",
                                    backgroundColor: "rgba(245, 158, 11, 0.1)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <Article sx={{ color: "#f59e0b", fontSize: "1rem" }} />
                                </Box>
                              )}
                            </Box>
                            <ListItemText
                              primary={
                                <Typography variant="body2" sx={{ fontWeight: 500, color: "#374151" }}>
                                  {chapter.title}
                                </Typography>
                              }
                              secondary={
                                <Typography variant="caption" sx={{ color: "#6b7280" }}>
                                  {chapter.length || "0h 0m"}
                                </Typography>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  </Box>
                ))}
              </List>
            </Box>
            </Paper>
          )}


          {/* Modern Content Area */}
          <Box sx={{ 
            flex: 1, 
            display: "flex", 
            flexDirection: "column", 
            gap: 0,
            minWidth: 0,
            width: "100%",
            transition: "all 0.3s ease",
            marginLeft: sidebarOpen ? 0 : 0,
          }}>
            {/* Default Welcome State - Show when no chapter is selected */}
            {!chapterId && (
              <Paper
                sx={{
                  borderRadius: 0,
                  p: 0,
                  minHeight: "70vh",
                  background: "rgba(255, 255, 255, 0.95)",
                  backdropFilter: "blur(20px)",
                  boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                  border: "1px solid rgba(255,255,255,0.2)",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "70vh",
                    background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  {/* Background decorative elements */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: "-20%",
                      right: "-20%",
                      width: "60%",
                      height: "60%",
                      background: "radial-gradient(circle, rgba(102, 126, 234, 0.1) 0%, transparent 70%)",
                      borderRadius: "50%",
                      animation: "pulse 4s ease-in-out infinite",
                      "@keyframes pulse": {
                        "0%, 100%": { transform: "scale(1)", opacity: 0.3 },
                        "50%": { transform: "scale(1.1)", opacity: 0.1 },
                      },
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: "-10%",
                      left: "-10%",
                      width: "40%",
                      height: "40%",
                      background: "radial-gradient(circle, rgba(118, 75, 162, 0.1) 0%, transparent 70%)",
                      borderRadius: "50%",
                      animation: "pulse 4s ease-in-out infinite reverse",
                      "@keyframes pulse": {
                        "0%, 100%": { transform: "scale(1)", opacity: 0.3 },
                        "50%": { transform: "scale(1.1)", opacity: 0.1 },
                      },
                    }}
                  />
                  
                  <Box sx={{ textAlign: "center", position: "relative", zIndex: 1 }}>
                    <motion.div
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ duration: 0.6, ease: "easeOut" }}
                    >
                      <Box
                        sx={{
                          width: 120,
                          height: 120,
                          borderRadius: "50%",
                          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mx: "auto",
                          mb: 4,
                          boxShadow: "0 20px 40px rgba(102, 126, 234, 0.3)",
                          animation: "float 6s ease-in-out infinite",
                          "@keyframes float": {
                            "0%, 100%": { transform: "translateY(0px)" },
                            "50%": { transform: "translateY(-10px)" },
                          },
                        }}
                      >
                        <MenuBook sx={{ color: "white", fontSize: "3rem" }} />
                      </Box>
                    </motion.div>
                    
                    <motion.div
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.2, duration: 0.6 }}
                    >
                      <Typography 
                        variant="h4" 
                        sx={{ 
                          fontWeight: 700, 
                          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          backgroundClip: "text",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          mb: 2,
                        }}
                      >
                        Welcome to {course.name}
                      </Typography>
                    </motion.div>
                    
                    <motion.div
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.4, duration: 0.6 }}
                    >
                      <Typography variant="h6" sx={{ color: "#6b7280", mb: 3, maxWidth: "500px", mx: "auto" }}>
                        Ready to start your learning journey? Select a chapter from the sidebar to begin exploring the course content.
                      </Typography>
                    </motion.div>
                    
                    <motion.div
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.6, duration: 0.6 }}
                    >
                      <Box sx={{ display: "flex", gap: 3, justifyContent: "center", flexWrap: "wrap" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 2, bgcolor: "rgba(102, 126, 234, 0.1)", borderRadius: 2 }}>
                          <OndemandVideo sx={{ color: "#667eea", fontSize: "1.5rem" }} />
                          <Typography variant="body2" sx={{ color: "#667eea", fontWeight: 600 }}>
                            Video Lessons
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 2, bgcolor: "rgba(118, 75, 162, 0.1)", borderRadius: 2 }}>
                          <AssignmentTurnedIn sx={{ color: "#764ba2", fontSize: "1.5rem" }} />
                          <Typography variant="body2" sx={{ color: "#764ba2", fontWeight: 600 }}>
                            Interactive Quizzes
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 2, bgcolor: "rgba(245, 158, 11, 0.1)", borderRadius: 2 }}>
                          <Article sx={{ color: "#f59e0b", fontSize: "1.5rem" }} />
                          <Typography variant="body2" sx={{ color: "#f59e0b", fontWeight: 600 }}>
                            Reading Materials
                          </Typography>
                        </Box>
                      </Box>
                    </motion.div>
                  </Box>
                </Box>
              </Paper>
            )}

            {chapterType === "VIDEO" && chapterId && (
              <Paper
                sx={{
                  borderRadius: 0,
                  p: 0,
                  minHeight: "70vh",
                  background: "rgba(255, 255, 255, 0.95)",
                  backdropFilter: "blur(20px)",
                  boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                  border: "1px solid rgba(255,255,255,0.2)",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                {selectedVideoUrl ? (
                  <Box sx={{ position: "relative", width: "100%", height: { xs: 220, sm: 280, md: "70vh" } }}>
                    <iframe
                      width="100%"
                      height="100%"
                      src={selectedVideoUrl}
                      title="Chapter Video"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      style={{ borderRadius: "0px" }}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: { xs: 220, sm: 280, md: "70vh" },
                      background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
                    }}
                  >
                    <Box sx={{ textAlign: "center" }}>
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: "50%",
                          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mx: "auto",
                          mb: 3,
                        }}
                      >
                        <OndemandVideo sx={{ color: "white", fontSize: "2.5rem" }} />
                      </Box>
                      <Typography variant="h5" sx={{ fontWeight: 600, color: "#374151", mb: 1 }}>
                        🎬 Ready to Learn?
                      </Typography>
                      <Typography variant="body1" sx={{ color: "#6b7280" }}>
                        Select a chapter from the sidebar to start watching
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Paper>
            )}

            {chapterType === "QUIZ" && (
              <Paper
                sx={{
                  borderRadius: 0,
                  p: 3,
                  minHeight: 500,
                  background: "rgba(255, 255, 255, 0.95)",
                  backdropFilter: "blur(20px)",
                  boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                  border: "1px solid rgba(255,255,255,0.2)",
                }}
              >
                <PlayQuiz chapterId={chapterId} />
              </Paper>
            )}

            {chapterType === "ARTICLE" && (
              <Paper
                sx={{
                  borderRadius: 0,
                  p: 3,
                  minHeight: 500,
                  background: "rgba(255, 255, 255, 0.95)",
                  backdropFilter: "blur(20px)",
                  boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                  border: "1px solid rgba(255,255,255,0.2)",
                }}
              >
                <ShowArticle chapterId={chapterId} />
              </Paper>
            )}

            {/* Default Course Info Section - Show when no chapter is selected */}
            {!chapterId && (
              <Paper
                sx={{
                  p: 4,
                  borderRadius: 0,
                  background: "rgba(255, 255, 255, 0.95)",
                  backdropFilter: "blur(20px)",
                  boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                  border: "1px solid rgba(255,255,255,0.2)",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 0,
                      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <MenuBook sx={{ color: "white", fontSize: "1.5rem" }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: "#374151" }}>
                      Course Overview
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#6b7280" }}>
                      Get started with this comprehensive course
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 4, mb: 4 }}>
                  {/* Course Description */}
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: "#374151", mb: 2 }}>
                      About This Course
                    </Typography>
                    <Typography variant="body1" sx={{ color: "#6b7280", lineHeight: 1.6 }}>
                      {course.description || "This comprehensive course will take you through all the essential concepts and practical applications. Each module is carefully designed to build upon the previous one, ensuring a smooth learning experience."}
                    </Typography>
                  </Box>

                  {/* Course Stats */}
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: "#374151", mb: 2 }}>
                      Course Statistics
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 2, bgcolor: "rgba(102, 126, 234, 0.1)", borderRadius: 2 }}>
                        <MenuBook sx={{ color: "#667eea", fontSize: "1.5rem" }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: "#667eea" }}>
                            {course.modules?.length || 0} Modules
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#6b7280" }}>
                            Comprehensive learning units
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 2, bgcolor: "rgba(118, 75, 162, 0.1)", borderRadius: 2 }}>
                        <OndemandVideo sx={{ color: "#764ba2", fontSize: "1.5rem" }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: "#764ba2" }}>
                            {course.modules?.reduce((acc, module) => acc + (module.chapters?.length || 0), 0) || 0} Chapters
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#6b7280" }}>
                            Interactive learning content
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 2, bgcolor: "rgba(245, 158, 11, 0.1)", borderRadius: 2 }}>
                        <AssignmentTurnedIn sx={{ color: "#f59e0b", fontSize: "1.5rem" }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: "#f59e0b" }}>
                            Interactive Content
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#6b7280" }}>
                            Videos, quizzes, and articles
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 2, bgcolor: "rgba(16, 185, 129, 0.1)", borderRadius: 2 }}>
                        <MenuBook sx={{ color: "#10b981", fontSize: "1.5rem" }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: "#10b981" }}>
                            {course.length || "0h 0m"} Total Duration
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#6b7280" }}>
                            Complete course length
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* Getting Started Section */}
                <Box sx={{ p: 3, bgcolor: "rgba(102, 126, 234, 0.05)", borderRadius: 2, border: "1px solid rgba(102, 126, 234, 0.1)" }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: "#374151", mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                    🚀 Getting Started
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#6b7280", mb: 2 }}>
                    Ready to begin your learning journey? Here's how to get the most out of this course:
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, m: 0 }}>
                    <Typography component="li" variant="body2" sx={{ color: "#6b7280", mb: 1 }}>
                      <strong>Start with Module 1:</strong> Begin with the first module to understand the course structure
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ color: "#6b7280", mb: 1 }}>
                      <strong>Follow the sequence:</strong> Complete chapters in order for the best learning experience
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ color: "#6b7280", mb: 1 }}>
                      <strong>Take notes:</strong> Use the comment section to ask questions and share insights
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ color: "#6b7280" }}>
                      <strong>Practice regularly:</strong> Complete quizzes and assignments to reinforce your learning
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            )}

            {/* Modern Comments Section */}
            {chapterId && (
              <Paper
                sx={{
                  p: 4,
                  borderRadius: 0,
                  background: "rgba(255, 255, 255, 0.95)",
                  backdropFilter: "blur(20px)",
                  boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                  border: "1px solid rgba(255,255,255,0.2)",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 0,
                      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Reply sx={{ color: "white", fontSize: "1.5rem" }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: "#374151" }}>
                      Discussion & Comments
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#6b7280" }}>
                      Share your thoughts and questions about this chapter
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                  <Avatar
                    sx={{
                      width: 48,
                      height: 48,
                      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      variant="outlined"
                      fullWidth
                      placeholder="Write a comment..."
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      multiline
                      minRows={3}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 0,
                          backgroundColor: 'rgba(255, 255, 255, 0.8)',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          },
                          '&.Mui-focused': {
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                          },
                        },
                      }}
                    />
                    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                      <Button
                        variant="contained"
                        onClick={handlePostComment}
                        sx={{
                          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          borderRadius: 0,
                          px: 4,
                          py: 1.5,
                          fontWeight: 600,
                          '&:hover': {
                            background: "linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)",
                          },
                        }}
                      >
                        Post Comment
                      </Button>
                    </Box>
                  </Box>
                </Box>

                {comments?.length === 0 ? (
                  <Box
                    sx={{
                      textAlign: "center",
                      py: 6,
                      px: 4,
                      backgroundColor: "rgba(102, 126, 234, 0.05)",
                      borderRadius: 0,
                      border: "2px dashed rgba(102, 126, 234, 0.2)",
                    }}
                  >
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: "50%",
                        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mx: "auto",
                        mb: 2,
                      }}
                    >
                      <Reply sx={{ color: "white", fontSize: "2rem" }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: "#374151", mb: 1 }}>
                      No comments yet
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#6b7280" }}>
                      Be the first to share your thoughts about this chapter!
                    </Typography>
                  </Box>
                ) : (
                comments.map((comment) => (
                  <motion.div
                    key={comment.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    style={{ marginBottom: "1rem" }}
                  >
                    <Paper
                      sx={{
                        p: 3,
                        mb: 2,
                        background: "rgba(255, 255, 255, 0.9)",
                        borderRadius: 0,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                        border: "1px solid rgba(226, 232, 240, 0.5)",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          boxShadow: "0 8px 20px rgba(0,0,0,0.1)",
                          transform: "translateY(-1px)",
                        },
                      }}
                    >
                      <Box display="flex" alignItems="center" mb={2}>
                        <Avatar
                          src={comment.userProfileImageUrl}
                          alt={comment.userName}
                          sx={{
                            width: 48,
                            height: 48,
                            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                            fontSize: "1.2rem",
                            fontWeight: "bold",
                          }}
                        >
                          {comment.userName ? comment.userName[0] : "U"}
                        </Avatar>
                        <Box ml={2}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#374151" }}>
                            {comment.userName}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#6b7280" }}>
                            {comment.createdAt}
                          </Typography>
                        </Box>
                      </Box>

                      <Typography variant="body1" sx={{ color: "#374151", lineHeight: 1.6, mb: 2 }}>
                        {comment.commentText}
                      </Typography>

                      {comment.replies?.map((reply) => (
                        <Paper
                          key={reply.id}
                          sx={{
                            p: 1.5,
                            mt: 1,
                            ml: 4,
                            background: "#f9f9ff",
                            borderLeft: "4px solid #3f51b5",
                            borderRadius: 0,
                          }}
                        >
                          <Box display="flex" alignItems="center" mb={0.5}>
                            <Avatar
                              src={reply.userProfileImageUrl}
                              sx={{ width: 24, height: 24 }}
                            >
                              {reply.userName ? reply.userName[0] : "U"}
                            </Avatar>
                            <Box ml={1}>
                              <Typography fontWeight="medium">
                                {reply.userName}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                {reply.createdAt}
                              </Typography>
                            </Box>
                          </Box>
                          <Typography variant="body2">
                            {reply.replyText}
                          </Typography>
                        </Paper>
                      ))}

                      <MuiLink
                        component="button"
                        variant="body2"
                        sx={{
                          ml: 4,
                          mt: 1,
                          display: "inline-flex",
                          alignItems: "center",
                        }}
                        onClick={() =>
                          setShowReplyBox({
                            ...showReplyBox,
                            [comment.id]: !showReplyBox[comment.id],
                          })
                        }
                      >
                        <Reply fontSize="small" sx={{ mr: 0.5 }} /> Reply
                      </MuiLink>

                      <Collapse
                        in={showReplyBox[comment.id]}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Box display="flex" gap={2} mt={1} ml={4}>
                          <TextField
                            size="small"
                            placeholder="Write a reply..."
                            value={replyTexts[comment.id] || ""}
                            onChange={(e) =>
                              setReplyTexts({
                                ...replyTexts,
                                [comment.id]: e.target.value,
                              })
                            }
                            fullWidth
                          />
                          <Button
                            variant="outlined"
                            onClick={() => handlePostReply(comment.id)}
                          >
                            Send
                          </Button>
                        </Box>
                      </Collapse>
                    </Paper>
                  </motion.div>
                ))
              )}
              </Paper>
            )}
          </Box>
        </Box>
      </Box>

      {/* Subscription Dialog */}
      <Dialog
        open={subscriptionDialogOpen}
        onClose={handleCloseSubscriptionDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
                          borderRadius: 0,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center',
          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          color: 'white',
          borderRadius: '0px 0px 0 0',
          py: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
            <Lock sx={{ fontSize: '2rem' }} />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Chapter Locked
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 2, color: '#374151' }}>
            🔒 This chapter requires a subscription
          </Typography>
          
          {lockedChapter && (
            <Box sx={{ 
              p: 3, 
              backgroundColor: '#f3f4f6', 
              borderRadius: 0,
              mb: 3,
              border: '1px solid #e5e7eb'
            }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                {lockedChapter.title}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {lockedChapter.type === "VIDEO" && "🎥 Video Content"}
                {lockedChapter.type === "ARTICLE" && "📄 Article Content"}
                {lockedChapter.type === "QUIZ" && "🧩 Quiz Content"}
              </Typography>
            </Box>
          )}
          
          <Typography variant="body1" sx={{ color: '#6b7280', mb: 3 }}>
            Subscribe to this course to access all chapters, including this one.
            You'll get unlimited access to all content, quizzes, and resources.
          </Typography>
          
          <Box sx={{ 
            p: 2, 
            backgroundColor: '#dbeafe', 
            borderRadius: 0,
            border: '1px solid #93c5fd'
          }}>
            <Typography variant="body2" sx={{ color: '#1e40af', fontWeight: 500 }}>
              💡 Tip: Preview chapters marked with the play icon are available for free!
            </Typography>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 2, justifyContent: 'center' }}>
          <Button
            onClick={handleCloseSubscriptionDialog}
            variant="outlined"
            sx={{
              borderColor: '#d1d5db',
              color: '#6b7280',
              px: 4,
              py: 1.5,
              '&:hover': {
                borderColor: '#9ca3af',
                backgroundColor: '#f9fafb'
              }
            }}
          >
            Maybe Later
          </Button>
          <Button
            onClick={handleSubscribeToCourse}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              color: 'white',
              px: 4,
              py: 1.5,
              fontWeight: 600,
              '&:hover': {
                background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
              }
            }}
          >
            Subscribe Now
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CourseCompleteDetail;
